import type { ComponentType } from 'react';

import { useSetAtom } from 'jotai';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import { setOpenArtifactAtom } from '../../../../data/projects/artifacts/state';
import { friendlyToolError } from '../../../../data/projects/components/projectChatMessage.helpers';
import { getWorkHostPort } from '../../../work-home/work-home-ports';

import { readToolResultRecord } from '../../../../contracts/projects/harness/toolResult';
import { readString, readToolData, readToolError } from './shared/readers';
import { toCreateTemplateStatus } from './status';

/**
 * Supplies the artefact-open effect the pin card no longer reaches for itself.
 * Projects owns where a template opens; the card only renders the affordance.
 */
type TemplateCardProps = {
  status: string;
  templateId?: string;
  templateName?: string;
  error?: string;
  variant?: string;
  onOpenTemplate?: (templateId: string) => void;
};

// eslint-disable-next-line react-refresh/only-export-components -- this module's exports are tool renderers, not components, and the wrapper cannot become its own module: the projects UI intake ratchet reserves new modules here for @heidi/agents
const PinTemplateCard = (
  props: Omit<TemplateCardProps, 'onOpenTemplate'>
): JSX.Element => {
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const TemplatePinBlock = getWorkHostPort('shell')
    .TemplatePinBlock as ComponentType<TemplateCardProps>;

  return (
    <TemplatePinBlock
      {...props}
      onOpenTemplate={(templateId) =>
        setOpenArtifact({ type: 'template-builder', templateId })
      }
    />
  );
};

/** The same wiring for the open-template card, whose click opens the same panel. */
// eslint-disable-next-line react-refresh/only-export-components -- this module's exports are tool renderers, not components, and the wrapper cannot become its own module: the projects UI intake ratchet reserves new modules here for @heidi/agents
const OpenTemplateEditorCard = (
  props: Omit<TemplateCardProps, 'onOpenTemplate'>
): JSX.Element => {
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);
  const OpenTemplateEditorBlock = getWorkHostPort('shell')
    .OpenTemplateEditorBlock as ComponentType<TemplateCardProps>;

  return (
    <OpenTemplateEditorBlock
      {...props}
      onOpenTemplate={(templateId) =>
        setOpenArtifact({ type: 'template-builder', templateId })
      }
    />
  );
};

const readTemplatePayload = (item: ToolUseItem) => {
  const data = readToolData(item) ?? readToolResultRecord(item);
  return {
    templateId: readString(data, 'template_id') ?? readString(item.input, 'template_id'),
    templateName:
      readString(data, 'template_name') ?? readString(item.input, 'template_name'),
  };
};

export const renderOpenTemplateEditorToolUse = (
  item: ToolUseItem
): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);

  // Without anything to identify the template the rich block carries no more
  // information than GenericToolUseRow — fall through cleanly.
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <OpenTemplateEditorCard
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <OpenTemplateEditorCard
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};

export const renderPinTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <PinTemplateCard
        variant="pin"
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <PinTemplateCard
      variant="pin"
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};

export const renderUnpinTemplateToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const { templateId, templateName } = readTemplatePayload(item);
  if (!templateId && !templateName && status !== 'error') return null;

  if (status === 'error') {
    return (
      <PinTemplateCard
        variant="unpin"
        status="error"
        templateId={templateId}
        templateName={templateName}
        error={friendlyToolError(readToolError(item))}
      />
    );
  }

  return (
    <PinTemplateCard
      variant="unpin"
      status={status}
      templateId={templateId}
      templateName={templateName}
    />
  );
};
